// src/components/TemplateFormFields.jsx
//
// Shared form body for creating/editing a Mail_Notification_Templates
// row. Used by both:
//   - EmailConfiguration.jsx's Edit Template modal (Screen 2's inline
//     edit -- editing an existing template stays a modal)
//   - TemplateCreatePage.jsx (Screen 1 -- "Create / upload template" is
//     its own full page, per the flow requested for the Template tab)
//
// Pulling the fields out into one component means both places render
// literally the same markup/behavior instead of two copies that can
// silently drift (e.g. one preview button working, the other not).
import React, { useMemo } from "react";

export const RECIPIENT_OPTIONS = ["User", "Admin"];

export const emptyTemplateForm = {
  Template_Code: "",
  Template_Name: "",
  Template_Description: "",
  Template_Type: "",
  Version: "1.0",
  Recipient_To: "User",
  Recipient_CC: "",
  Recipient_BCC: "",
  Subject: "",
  Body_Html: "",
  Placeholders: "",
  Is_Active: true
};

// readOnly (new): renders every field disabled/non-interactive, for the
// Action column's "View" button -- same idea as RateCard.jsx's View
// modal, which mirrors its Edit modal's layout but with every field
// locked. The "Show Preview" toggle is left clickable even in view mode
// since it doesn't edit any data, just shows/hides the rendered HTML.
export default function TemplateFormFields({ form, setForm, isEdit, showPreview, setShowPreview, readOnly = false }) {
  const previewHtml = useMemo(() => form.Body_Html, [form.Body_Html]);
  const fieldClass = `form-control px-3${readOnly ? " bg-light" : ""}`;
  const selectClass = `form-select${readOnly ? " bg-light" : ""}`;

  return (
    <>
      <div className="row g-3 mb-3">
        {!isEdit ? (
          <div className="col-md-6">
            <label className="form-label fw-medium">Template Code :</label>
            <input
              className="form-control px-3"
              type="text"
              placeholder="e.g. CET_WELCOME_EMAIL"
              value={form.Template_Code}
              onChange={(e) => setForm((f) => ({ ...f, Template_Code: e.target.value.toUpperCase().replace(/\s+/g, "_") }))}
            />
            <small className="text-muted">A stable internal name. Can't be changed after creation.</small>
          </div>
        ) : (
          <div className="col-md-6">
            <label className="form-label fw-medium">Template Code :</label>
            <input className="form-control px-3 bg-light" type="text" value={form.Template_Code} readOnly disabled />
          </div>
        )}
        <div className="col-md-6">
          <label className="form-label fw-medium">Template Name :</label>
          <input
            className={fieldClass}
            type="text"
            placeholder="Template Name"
            value={form.Template_Name}
            onChange={(e) => setForm((f) => ({ ...f, Template_Name: e.target.value }))}
            readOnly={readOnly}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-8">
          <label className="form-label fw-medium">Template Description :</label>
          <input
            className={fieldClass}
            type="text"
            placeholder="Short description of when/why this template is used"
            value={form.Template_Description}
            onChange={(e) => setForm((f) => ({ ...f, Template_Description: e.target.value }))}
            readOnly={readOnly}
            disabled={readOnly}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label fw-medium">Template Type :</label>
          <input
            className={fieldClass}
            type="text"
            placeholder="e.g. OTP"
            value={form.Template_Type}
            onChange={(e) => setForm((f) => ({ ...f, Template_Type: e.target.value }))}
            readOnly={readOnly}
            disabled={readOnly}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label fw-medium">Version :</label>
          <input
            className={fieldClass}
            type="text"
            placeholder="1.0"
            value={form.Version}
            onChange={(e) => setForm((f) => ({ ...f, Version: e.target.value }))}
            readOnly={readOnly}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <label className="form-label fw-medium">Recipient To :</label>
          <select
            className={selectClass}
            value={form.Recipient_To}
            onChange={(e) => setForm((f) => ({ ...f, Recipient_To: e.target.value }))}
            disabled={readOnly}
          >
            {RECIPIENT_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label fw-medium">Recipient CC :</label>
          <select
            className={selectClass}
            value={form.Recipient_CC}
            onChange={(e) => setForm((f) => ({ ...f, Recipient_CC: e.target.value }))}
            disabled={readOnly}
          >
            <option value="">None</option>
            {RECIPIENT_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label fw-medium">Recipient BCC :</label>
          <select
            className={selectClass}
            value={form.Recipient_BCC}
            onChange={(e) => setForm((f) => ({ ...f, Recipient_BCC: e.target.value }))}
            disabled={readOnly}
          >
            <option value="">None</option>
            {RECIPIENT_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label fw-medium">Subject :</label>
        <input
          className={fieldClass}
          type="text"
          placeholder="Email subject line"
          value={form.Subject}
          onChange={(e) => setForm((f) => ({ ...f, Subject: e.target.value }))}
          readOnly={readOnly}
          disabled={readOnly}
        />
      </div>

      <div className="mb-3">
        <div className="d-flex align-items-center justify-content-between">
          <label className="form-label fw-medium mb-1">Body (HTML) :</label>
          <button type="button" className="btn btn-sm btn-light border" onClick={() => setShowPreview((s) => !s)}>
            {showPreview ? "Hide Preview" : "Show Preview"}
          </button>
        </div>
        <div className="card border">
          <div className="card-body p-0">
            <textarea
              className={`form-control border-0 rounded-0 p-3${readOnly ? " bg-light" : ""}`}
              rows={8}
              placeholder="Write your email body here. Use {{PlaceholderName}} for dynamic values."
              value={form.Body_Html}
              onChange={(e) => setForm((f) => ({ ...f, Body_Html: e.target.value }))}
              readOnly={readOnly}
              disabled={readOnly}
            />
          </div>
        </div>
        {showPreview && (
          <div className="border rounded p-3 mt-2 bg-light">
            <div className="text-muted fs-12 mb-2">Preview</div>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        )}
      </div>

      <div className="row g-3 align-items-end">
        <div className="col-md-8">
          <label className="form-label fw-medium">Placeholders :</label>
          <input
            className={fieldClass}
            type="text"
            placeholder="Comma-separated, e.g. Username,ActivationLink"
            value={form.Placeholders}
            onChange={(e) => setForm((f) => ({ ...f, Placeholders: e.target.value }))}
            readOnly={readOnly}
            disabled={readOnly}
          />
        </div>
        <div className="col-md-4">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="template-is-active"
              checked={form.Is_Active}
              onChange={(e) => setForm((f) => ({ ...f, Is_Active: e.target.checked }))}
              disabled={readOnly}
            />
            <label className="form-check-label fw-medium" htmlFor="template-is-active">Active</label>
          </div>
        </div>
      </div>
    </>
  );
}
